import React, { useState } from 'react'
import PropTypes from 'prop-types'
import PopperJS from '@popperjs/core'
import { PROP_TYPE_CHILDREN } from '../constants'
import { ArrayElement } from '../helpers'

export const PLACEMENTS = [
  'bottom',
  'bottom-end',
  'bottom-start',
  'left',
  'left-end',
  'left-start',
  'right',
  'right-end',
  'right-start',
  'top',
  'top-end',
  'top-start',
] as const

export type TooltipPlacement = ArrayElement<typeof PLACEMENTS>

interface TooltipContextValue {
  toggleElement: Element | PopperJS.VirtualElement | null
  setToggleElement: (el: Element | PopperJS.VirtualElement | null) => void
  tooltipElement: HTMLElement | null
  setTooltipElement: (el: HTMLElement | null) => void
  arrowElement: HTMLElement | null
  setArrowElement: (el: HTMLElement | null) => void

  placement: TooltipPlacement
  positionFixed: boolean

  hideTooltip: () => void
  showTooltip: () => void
  show: boolean
}

export const TooltipContext = React.createContext<TooltipContextValue>({
  toggleElement: null,
  setToggleElement: () => {},
  tooltipElement: null,
  setTooltipElement: () => {},
  arrowElement: null,
  setArrowElement: () => {},

  placement: 'top',
  positionFixed: false,

  hideTooltip: () => {},
  showTooltip: () => {},
  show: false,
})

export const { Provider, Consumer } = TooltipContext

const PROP_TYPE_PLACEMENT = PropTypes.oneOf(PLACEMENTS)

interface TooltipProps {
  placement?: TooltipPlacement
  positionFixed?: boolean
  children: React.ReactNode
}

const Tooltip = ({
  children,
  placement = 'top',
  positionFixed = false,
}: TooltipProps) => {
  const [show, setShow] = useState<boolean>(false)
  const [toggleElement, setToggleElement] = useState<
    Element | PopperJS.VirtualElement | null
  >(null)
  const [tooltipElement, setTooltipElement] = useState<HTMLElement | null>(null)
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>(null)

  const hideTooltip = () => setShow(false)
  const showTooltip = () => setShow(true)

  return (
    <Provider
      value={{
        toggleElement,
        setToggleElement,
        tooltipElement,
        setTooltipElement,
        arrowElement,
        setArrowElement,

        placement,
        positionFixed,

        hideTooltip,
        showTooltip,
        show,
      }}
    >
      {children}
    </Provider>
  )
}

Tooltip.propTypes = {
  children: PROP_TYPE_CHILDREN,
  placement: PROP_TYPE_PLACEMENT,
  positionFixed: PropTypes.bool,
}

export default Tooltip
